import { useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import type { DownloadPolicyReviewAttachmentInput, PolicyReviewRecordDTO } from '@deepguide-ai/types'
import { trpc } from '@/lib/trpc'
import { toast } from 'sonner'
import { Button } from '@deepguide-ai/ui/button'
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@deepguide-ai/ui/dialog'
import { PolicyReviewHistory } from '@/components/screenata/policy-detail/PolicyReviewHistory'
import { EvidenceFilePreviewDialog } from './EvidenceFilePreviewDialog'
import { getFileType } from '@/components/evidence-viewer'
import { downloadFile } from '@/lib/download-file'

export function AuditorPolicyReviewHistory({ auditCycleId, source, sourceId, title, records, generatedAt, documentId }: {
  auditCycleId: string
  source: DownloadPolicyReviewAttachmentInput['source']
  sourceId: string
  title: string
  records: Omit<PolicyReviewRecordDTO, 'policyDocumentVersionId'>[]
  documentId?: string
  generatedAt?: string
}) {
  const [open, setOpen] = useState(false)
  const [pending, setPending] = useState(false)
  const [file, setFile] = useState<{ url: string; filename: string; mimeType: string } | null>(null)
  const queryClient = useQueryClient()
  async function openAttachment(reviewId: string, fileId: string, download = false) {
    setPending(true)
    try {
      const result = await queryClient.fetchQuery({
        ...trpc.auditor.downloadPolicyReviewAttachment.queryOptions({ auditCycleId, source, sourceId, reviewId, fileId, ...(documentId ? { documentId } : {}) }),
        staleTime: 0,
      })
      if (download || getFileType(result.mimeType, result.fileName) === 'other') {
        await downloadFile(result.url, result.fileName, 'omit')
      } else {
        setFile({ url: result.url, filename: result.fileName, mimeType: result.mimeType })
      }
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Review attachment is unavailable')
    } finally {
      setPending(false)
    }
  }
  return <>
    <Button variant='ghost' size='sm' onClick={() => setOpen(true)}>{source === 'report' ? 'View history' : 'Review history'}</Button>
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className='max-h-[85vh] overflow-y-auto'>
        <DialogHeader>
          <DialogTitle>{title} — Review history</DialogTitle>
          <DialogDescription>{source === 'document'
            ? `Read-only document reviews frozen with this evidence submission${generatedAt ? ` on ${new Date(generatedAt).toLocaleString()}` : ''}. Later reviews do not change this history. These are not Test approvals.`
            : generatedAt
            ? `Source records frozen when this report was generated on ${new Date(generatedAt).toLocaleString()}.`
            : 'Read-only approvals and reviews for this policy version.'}</DialogDescription>
        </DialogHeader>
        <div className='px-6'>
          <PolicyReviewHistory records={records} onOpenAttachment={openAttachment} onDownloadAttachment={(reviewId, fileId) => openAttachment(reviewId, fileId, true)} attachmentPending={pending} />
        </div>
        <DialogFooter><Button variant='outline' onClick={() => setOpen(false)}>Close</Button></DialogFooter>
      </DialogContent>
    </Dialog>
    <EvidenceFilePreviewDialog file={file} onClose={() => setFile(null)} />
  </>
}
